import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import {
  columnForStatus,
  MyTasksKanbanBoard,
  MyTasksKanbanColumnId,
} from '../../components/tasks/MyTasksKanbanBoard';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollaboratorAutocomplete } from '../../components/ui/CollaboratorAutocomplete';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { TaskAreaPicker } from '../../components/strategic-planning/TaskAreaPicker';
import {
  assignMyTask,
  duplicateMyTask,
  finishMyTask,
  listMyTaskParentOptions,
  listMyTasksInventory,
  MyTaskListItem,
  pauseMyTask,
  relinkMyTask,
  replaceMyTaskAreas,
  startMyTask,
  TaskParentLinkOption,
} from '../../services/myTasksApi';
import { formatPlanningTaskStatusLabel } from '../../types/PlanningTaskStatus';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

type InventoryViewId = 'list' | 'kanban';

const VIEW_TABS: SettingsTabItem<InventoryViewId>[] = [
  { id: 'list', label: 'Lista' },
  { id: 'kanban', label: 'Kanban' },
];

function statusForColumn(column: MyTasksKanbanColumnId): string {
  if (column === 'Doing') return 'InProgress';
  if (column === 'Done') return 'Completed';
  if (column === 'ToDo') return 'Overdue';
  return 'Planned';
}

export function TasksInventoryPage() {
  const [items, setItems] = useState<MyTaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<InventoryViewId>('list');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [withoutParentOnly, setWithoutParentOnly] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MyTaskListItem | null>(null);
  const [parentOptions, setParentOptions] = useState<TaskParentLinkOption[]>([]);
  const [parentWorkItemId, setParentWorkItemId] = useState('');
  const [saving, setSaving] = useState(false);
  const [movingId, setMovingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listMyTasksInventory({
        unassignedOnly: unassignedOnly || undefined,
        withoutParentOnly: withoutParentOnly || undefined,
      });
      setItems(result.items);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar inventário de tarefas.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [unassignedOnly, withoutParentOnly]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!selectedTask) return;
    setParentWorkItemId(selectedTask.parentWorkItemId ?? '');
    void listMyTaskParentOptions()
      .then(setParentOptions)
      .catch(() => setParentOptions([]));
  }, [selectedTask]);

  async function handleKanbanMove(item: MyTaskListItem, column: MyTasksKanbanColumnId): Promise<void> {
    const targetStatus = statusForColumn(column);
    if (item.status === targetStatus) return;
    if (item.isUnassigned) {
      toast.info('Atribua um responsável antes de mover a tarefa no kanban.');
      return;
    }

    const previous = items;
    setMovingId(item.id);
    setItems((current) =>
      current.map((entry) => (entry.id === item.id ? { ...entry, status: targetStatus } : entry)),
    );

    try {
      if (column === 'Doing') {
        if (!item.canStart) throw new Error('Não é possível iniciar este item.');
        await startMyTask(item.id);
      } else if (column === 'Backlog' || column === 'ToDo') {
        if (item.status === 'InProgress') {
          if (!item.canPause) throw new Error('Não é possível pausar este item.');
          await pauseMyTask(item.id);
        } else {
          throw new Error('Movimento não suportado para este status.');
        }
      } else if (column === 'Done') {
        if (!item.canFinish) throw new Error('Só tarefas em andamento podem ser concluídas no quadro.');
        await finishMyTask(item.id, 'Fulfilled');
      }

      toast.success('Status atualizado.');
      await reload();
    } catch (error) {
      setItems(previous);
      toast.error(getRequestErrorMessage(error, 'Falha ao mover item no kanban.'));
    } finally {
      setMovingId(null);
    }
  }

  async function handleDuplicate(): Promise<void> {
    if (!selectedTask) return;
    setSaving(true);
    try {
      await duplicateMyTask(selectedTask.id);
      toast.success('Tarefa duplicada sem responsável.');
      setSelectedTask(null);
      await reload();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível duplicar a tarefa.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign(ownerUserId: string): Promise<void> {
    if (!selectedTask || !ownerUserId) return;
    setSaving(true);
    try {
      await assignMyTask(selectedTask.id, ownerUserId);
      toast.success('Responsável atribuído.');
      setSelectedTask(null);
      await reload();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atribuir responsável.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleRelink(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!selectedTask || !parentWorkItemId) return;
    const parent = parentOptions.find((item) => item.id === parentWorkItemId);
    setSaving(true);
    try {
      await relinkMyTask(selectedTask.id, {
        parentWorkItemId,
        objectiveId: parent?.objectiveId,
      });
      toast.success('Tarefa vinculada à meta/marco.');
      setSelectedTask(null);
      await reload();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao vincular tarefa.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Inventário de tarefas"
        description="Todas as tarefas do seu departamento — inclusive sem responsável ou sem meta/marco. Nada fica perdido."
        actions={
          <Link to="/tasks" className="erp-btn-secondary">
            Voltar às minhas tarefas
          </Link>
        }
      />

      <div className="mb-4 space-y-3 rounded-xl border border-secondary-200 bg-white p-4">
        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={unassignedOnly}
              onChange={(event) => setUnassignedOnly(event.target.checked)}
            />
            Somente sem responsável
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={withoutParentOnly}
              onChange={(event) => setWithoutParentOnly(event.target.checked)}
            />
            Somente sem meta/marco
          </label>
        </div>
        <p className="text-xs text-secondary-500">
          Escopo limitado às equipes do seu departamento. Outras áreas não veem estas tarefas.
        </p>
      </div>

      <SettingsTabBar tabs={VIEW_TABS} activeTab={view} onChange={setView} />

      {loading ? (
        <p className="text-secondary-500">Carregando inventário...</p>
      ) : view === 'list' ? (
        <section className="rounded-xl border border-secondary-200 bg-white p-4">
          <div className="overflow-x-auto">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Responsável</th>
                  <th>Vínculo</th>
                  <th>Status</th>
                  <th>Prazo</th>
                  <th className="w-40">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-secondary-500">
                      Nenhuma tarefa no escopo do departamento com os filtros atuais.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td className="font-medium text-secondary-900">
                        {item.title}
                        {item.isUnassigned ? (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            Sem responsável
                          </span>
                        ) : null}
                        {item.isOrphan ? (
                          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                            Sem meta/marco
                          </span>
                        ) : null}
                      </td>
                      <td>{item.ownerUserName || 'Não atribuído'}</td>
                      <td>
                        {item.parentWorkItemTitle
                          ? `${item.parentWorkItemKind ?? 'Pai'}: ${item.parentWorkItemTitle}`
                          : '—'}
                      </td>
                      <td>
                        <CodedStatusBadge
                          status={item.status}
                          label={formatPlanningTaskStatusLabel(item.status)}
                        />
                      </td>
                      <td>{item.plannedEndDate ? formatDateBr(item.plannedEndDate) : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="erp-btn-secondary text-sm"
                          onClick={() => setSelectedTask(item)}
                        >
                          Recuperar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <MyTasksKanbanBoard
          items={items}
          movingId={movingId}
          onSelect={setSelectedTask}
          onMove={(item, column) => {
            void handleKanbanMove(item, column);
          }}
        />
      )}

      {selectedTask ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-secondary-900">{selectedTask.title}</h2>
                <p className="text-sm text-secondary-500">
                  {selectedTask.ownerUserName || 'Não atribuído'} · {selectedTask.status} ·{' '}
                  {columnForStatus(selectedTask.status)}
                </p>
              </div>
              <button
                type="button"
                className="erp-btn-secondary text-sm"
                onClick={() => setSelectedTask(null)}
              >
                Fechar
              </button>
            </div>

            {selectedTask.kind === 'Tarefa' ? (
              <button
                type="button"
                className="erp-btn-secondary mb-4 text-sm"
                disabled={saving}
                onClick={() => void handleDuplicate()}
              >
                Duplicar tarefa
              </button>
            ) : null}

            {selectedTask.kind === 'Tarefa' ? (
              <div className="mb-4">
                <TaskAreaPicker
                  selectedIds={selectedTask.areas ?? []}
                  disabled={saving}
                  onChange={(areas) => {
                    void (async () => {
                      setSaving(true);
                      try {
                        await replaceMyTaskAreas(selectedTask.id, areas);
                        toast.success('Áreas da tarefa atualizadas.');
                        setSelectedTask((current) => (current ? { ...current, areas } : current));
                        await reload();
                      } catch (error) {
                        toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar as áreas da tarefa.'));
                      } finally {
                        setSaving(false);
                      }
                    })();
                  }}
                />
              </div>
            ) : null}

            {selectedTask.canAssign ? (
              <div
                className={`mb-4 space-y-2 rounded-xl border p-3 ${
                  selectedTask.ownerUserId ? 'border-secondary-200' : 'border-amber-300 bg-amber-50/70'
                }`}
              >
                <p className="text-sm font-semibold text-secondary-800">
                  Responsável <RequiredMark />
                </p>
                <p className="text-xs text-secondary-600">
                  Clique no campo, busque pelo nome e clique fora para salvar.
                </p>
                <CollaboratorAutocomplete
                  valueUserId={selectedTask.ownerUserId}
                  valueUserName={selectedTask.ownerUserName}
                  disabled={saving}
                  saving={saving}
                  onCommit={(user) => {
                    if (!user || user.userId === selectedTask.ownerUserId) return;
                    void handleAssign(user.userId);
                  }}
                />
              </div>
            ) : null}

            {selectedTask.canRelink ? (
              <form className="space-y-2 rounded-lg border border-secondary-200 p-3" onSubmit={handleRelink}>
                <p className="text-sm font-medium text-secondary-800">Vincular a meta ou marco</p>
                <select
                  className="erp-input"
                  value={parentWorkItemId}
                  onChange={(event) => setParentWorkItemId(event.target.value)}
                  required
                >
                  <option value="">Selecione meta, marco ou tarefa pai...</option>
                  {parentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      [{option.kind}] {option.title} — {option.objectiveTitle}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="erp-btn-secondary text-sm"
                  disabled={saving || !parentWorkItemId}
                >
                  Vincular
                </button>
              </form>
            ) : null}
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
